import React from 'react';
import { reduxForm, SubmissionError } from 'redux-form';
import _ from 'lodash';

const wrapSubmitFunction = originalSubmitFunction => (...args) => {
  try {
    return originalSubmitFunction(...args);
  } catch (err) {
    /* eslint-disable no-console */
    if (err instanceof SubmissionError) {
      console.error('Throwing submission error');
      throw err;
    }
    console.error('Encountered error in onSubmit function', err);
    return null;
    /* eslint-enable no-console */
  }
};

function wrapHandleSubmit(originalHandleSubmit) {
  // The wrapped version of handleSubmit is a function that calls the original handleSubmit function by wraping the first argument (onSubmit)
  return submitOrEvent => {
    if (submitOrEvent && _.isFunction(submitOrEvent)) {
      return originalHandleSubmit(wrapSubmitFunction(submitOrEvent));
    } else {
      return originalHandleSubmit(submitOrEvent);
    }
  };
}

const wrapHandleSubmitWithDebug = underlyingComponent => {
  // Wrapper is a component that renders the underlying component with a wrapped version of handleSubmit
  const Wrapper = props => {
    const enhancedProps = { ...props, handleSubmit: wrapHandleSubmit(props.handleSubmit) };
    return React.createElement(underlyingComponent, enhancedProps);
  };
  Wrapper.displayName = 'DebugWrapperBetweenReduxFormAndYourComponent';
  return Wrapper;
};

const wrapReduxFormComponent = ReduxFormComponent => {
  const wrapper = ({ onSubmit, ...props } = {}) => {
    const modifiedProps = props;
    if (onSubmit) {
      modifiedProps.onSubmit = wrapSubmitFunction(onSubmit);
    }
    return <ReduxFormComponent {...modifiedProps} />;
  };
  wrapper.displayName = 'DebugWrapperAroundReduxFormComponent';
  return wrapper;
};

const reduxFormWithDebugging = ({ onSubmit, ...options } = {}) => {
  const modifiedOptions = options;
  if (onSubmit) {
    modifiedOptions.onSubmit = wrapSubmitFunction(onSubmit);
  }
  // This handles the case when onSubmit is passed directly to reduxForm HOC factory
  const wrappedReduxForm = reduxForm(modifiedOptions);

  return underlyingComponent => {
    // This handles the case when onSubmit is passed as the first argument to handleSubmit
    const WrapperAroundUnderlyingComponent = wrapHandleSubmitWithDebug(underlyingComponent);
    const ReduxFormComponent = wrappedReduxForm(WrapperAroundUnderlyingComponent);

    // This handles the case when onSubmit is passed as a prop to the redux formified component
    const WrapperAroundReduxFormComponent = wrapReduxFormComponent(ReduxFormComponent);
    return WrapperAroundReduxFormComponent;
  };
};

export default reduxFormWithDebugging;
